import { colors } from '@lmc/common/constants'
import type { Carer } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { hexToRgbA } from '../../../../../common/utils'
import { NAMESPACE } from '../../../../../locale'
import { riskAssessmentsConstants } from '../../../../../locale/riskAssessments'

interface BackNavigationHeaderProps {
  onBack: () => void
  archiveMetadata: {
    archivedAt: string
    archivedBy: string
    reason: string
  } | null
}

interface ArchiveBannerProps {
  archiveMetadata: {
    archivedAt: string
    archivedBy: string
    reason: string
  }
}

const ArchiveBannerComponent: React.FC<ArchiveBannerProps> = ({
  archiveMetadata
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS)
  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const getCarerName = (carerId: string) => {
    const carer = carers?.find((carer) => carer.id === carerId)
    return carer ? `${carer.name?.first} ${carer.name?.last}` : carerId
  }

  const formatDate = (isoDate: string) => {
    return dayjs(isoDate).format('DD-MM-YYYY HH:mm')
  }

  const { archivedAt, archivedBy, reason } = archiveMetadata
  const carerName = getCarerName(archivedBy)
  const formattedDate = formatDate(archivedAt)

  return (
    <ArchiveBanner>
      <ArchiveText>
        <StyledText level={1} weight="bold">
          {t(riskAssessmentsConstants.ARCHIVED_BY, { carerName })}
        </StyledText>
        <StyledText level={1}>{formattedDate}. </StyledText>
        <StyledText level={1} weight="bold">
          {t(riskAssessmentsConstants.REASON)}
        </StyledText>
        <StyledText level={1}>{reason}</StyledText>
      </ArchiveText>
    </ArchiveBanner>
  )
}

const BackNavigationHeader: React.FC<BackNavigationHeaderProps> = ({
  onBack,
  archiveMetadata = null
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS)
  const riskArchivingFlagEnabled = useFeatureFlags({
    flagNames: ['risk_assessments_archiving']
  })?.data.risk_assessments_archiving.enabled

  return (
    <>
      <HeaderContainer>
        <BackButton onClick={onBack}>
          <span className="octicon octicon-chevron-left" />{' '}
          {t(riskAssessmentsConstants.BACK)}
        </BackButton>
      </HeaderContainer>
      {archiveMetadata &&
        Object.keys(archiveMetadata).length > 0 &&
        riskArchivingFlagEnabled && (
          <ArchiveBannerComponent archiveMetadata={archiveMetadata} />
        )}
    </>
  )
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const BackButton = styled.a`
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const ArchiveBanner = styled.div`
  border-radius: 4px;
  margin-top: 16px;
  padding: 13px;
  width: 100%;
  background: ${hexToRgbA(colors.informationblue, 0.05)};
  border: 1px solid ${hexToRgbA(colors.informationblue, 0.2)};
`

const ArchiveText = styled.div`
  font-size: 16px;
  line-height: 24px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 16px;
  }
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`
export default BackNavigationHeader
